import {
  Grid,
  IconButton,
  Button,
  Box,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import AttachmentIcon from "@mui/icons-material/Attachment";
import DeleteIcon from "@mui/icons-material/Delete";

import { useEffect, useState, useCallback } from "react";
import DatagridCustom from "../General/DatagridCustom";
import { useAuth } from "../../context/AuthContext";

import {
  getAttachments,
  downloadAttachment,
  deleteAttachment,
} from "../../services/LegalFileAttachmentService";

import AppDialog from "../General/Dialog/AppDialog";
import RetryEscritoButton from "./RetryEscritoButton";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import { useToast } from "../../context/ToastContext";

const STATUS_OPTIONS = [
  { value: "",           label: "Todos",        color: "default" },
  { value: "generated",  label: "Generado",     color: "info"    },
  { value: "preloaded",  label: "Precargado",   color: "warning" },
  { value: "uploaded",   label: "Cargado",      color: "success" },
  { value: "failed",     label: "Fallido",      color: "error"   },
];

function PivotStatusChip({ value }) {
  if (value === "uploaded")  return <Chip label="CARGADO"     color="success" variant="outlined" size="small" />;
  if (value === "preloaded") return <Chip label="PRECARGADO"  color="warning" variant="outlined" size="small" />;
  if (value === "failed")    return <Chip label="FALLIDO"     color="error"   variant="outlined" size="small" />;
  if (value === "generated") return <Chip label="GENERADO"    color="info"    variant="outlined" size="small" />;
  return <Chip label={value || "SIN ESTADO"} variant="outlined" size="small" />;
}

export default function AttachmentLegalFile() {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [statusFilter, setStatusFilter] = useState("");

  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [deletingAttachment, setDeletingAttachment] = useState(false);

  const { hasPermission } = useAuth();

  const fetchAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const search = filterModel.quickFilterValues?.join(" ") || "";
      const response = await getAttachments({
        page: paginationModel.page + 1,
        perPage: paginationModel.pageSize,
        search,
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      setRows(response.data.data);
      setTotal(response.data.total);
    } catch {
      showToast("No se pudieron cargar los escritos.", "error")
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, statusFilter, showToast]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  // reset page when status filter changes
  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePreview = async (id) => {
    try {
      setLoading(true);
      const response = await downloadAttachment(id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      setPreviewUrl(URL.createObjectURL(blob));
      setOpenPreview(true);
    } catch {
      showToast("No se pudo cargar el archivo.", "error")
    } finally {
      setLoading(false);
    }
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleOpenDeleteConfirm = (row) => {
    setAttachmentToDelete(row);
    setOpenDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!attachmentToDelete?.id) return;
    try {
      setDeletingAttachment(true);
      const res = await deleteAttachment(attachmentToDelete.id);
      if (res.data?.status === "success") {
        showToast(res.data.message, "success")
        setOpenDeleteConfirm(false);
        setAttachmentToDelete(null);
        await fetchAttachments();
      }
    } catch {
      showToast("Error al eliminar adjunto.", "error")
    } finally {
      setDeletingAttachment(false);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Archivo",
      flex: 2.5,
    },
    {
      field: "type",
      headerName: "Tipo",
      flex: 2,
      valueGetter: (params) => params?.display_name || params?.name || "—",
    },
    {
      field: "created_at",
      headerName: "Fecha",
      flex: 1.5,
      valueGetter: (params) => {
        const date = new Date(params);
        if (isNaN(date.getTime())) return "—";
        const p = (n) => String(n).padStart(2, "0");
        return `${p(date.getDate())}/${p(date.getMonth() + 1)}/${date.getFullYear()} ${p(date.getHours())}:${p(date.getMinutes())}`;
      },
    },
    {
      field: "legal_file_code",
      headerName: "Expediente",
      flex: 1.5,
      renderCell: (params) => {
        const legalFileId = params.row.legal_file_id;
        const code = params.value || "—";

        if (!legalFileId) return <Typography variant="body2">{code}</Typography>;

        return (
          <Tooltip title="Ver detalle del expediente" placement="top">
            <Button
              component="a"
              href={`/legal/file/${legalFileId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              sx={{
                minWidth: "auto",
                p: 0,
                fontWeight: 900,
                textTransform: "none",
                justifyContent: "flex-start",
                color: "#7d7d7d",
              }}
            >
              {code}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      field: "pivot_status",
      headerName: "Estado",
      flex: 1.5,
      renderCell: (params) => <PivotStatusChip value={params.value} />,
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handlePreview(params.row.id)}
            disabled={!hasPermission("descargar archivos adjuntos expedientes")}
          >
            <VisibilityIcon />
          </IconButton>

          {params.row.pivot_status === "failed" && params.row.legal_file_id && (
            <RetryEscritoButton
              attachmentId={params.row.id}
              legalFileId={params.row.legal_file_id}
              escritoName={params.row.name}
              onDone={fetchAttachments}
            />
          )}

          <IconButton
            color="error"
            onClick={() => handleOpenDeleteConfirm(params.row)}
            disabled={!hasPermission("eliminar archivos adjuntos expedientes")}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const statusFilterBar = (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {STATUS_OPTIONS.map((opt) => (
        <Chip
          key={opt.value}
          label={opt.label}
          color={statusFilter === opt.value ? opt.color || "primary" : "default"}
          variant={statusFilter === opt.value ? "filled" : "outlined"}
          onClick={() => handleStatusFilter(opt.value)}
          sx={{ fontWeight: 700, cursor: "pointer" }}
        />
      ))}
    </Box>
  );

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12 }}>

        <DatagridCustom
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          title="Escritos de Expedientes"
          description="Listado de escritos PDF con su expediente y estado de carga."
          icon={<AttachmentIcon sx={{ fontSize: 32 }} />}
          headerActions={statusFilterBar}
          paginationModel={paginationModel}
          onPaginationChange={setPaginationModel}
          filterModel={filterModel}
          onFilterChange={setFilterModel}
          filterMode="server"
          total={total}
          loading={loading}
          isServerPagination={true}
        />
      </Grid>

      <AppDialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="lg"
        fullScreenMobile
        fullScreenBreakpoint="md"
        stickyHeader
        title="Vista previa del archivo"
        contentSx={{ p: 0, height: "80vh" }}
      >
        {previewUrl ? (
          <iframe
            src={previewUrl}
            title="PDF Preview"
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        ) : (
          <Typography sx={{ p: 2 }}>Cargando archivo...</Typography>
        )}
      </AppDialog>

      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={() => {
          setOpenDeleteConfirm(false);
          setAttachmentToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Confirmar eliminación"
        description={`¿Quieres eliminar el adjunto${
          attachmentToDelete?.name ? ` "${attachmentToDelete.name}"` : ""
        }? Esta acción no se puede deshacer.`}
        confirmText={deletingAttachment ? "Eliminando..." : "Eliminar"}
        cancelText="Cancelar"
        confirmColor="error"
        loading={deletingAttachment}
        fullScreenMobile
        fullScreenBreakpoint="md"
      />
    </Grid>
  );
}
